// The people in one budget group, under its row. Budget groups are pots of
// money, not the per-Boo access groups on a Boo's People page, so members show
// as round person chips with initials rather than the People page's square
// group tags.

import { Button } from "@/components/ui/button";
import { api, ApiError, type SpendDirectoryEntry, type SpendGroupRow } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { SpendPersonPicker } from "./SpendPersonPicker";

function initials(name: string): string {
  const parts = name.replace(/[^\p{L}\p{N} ]/gu, "").trim().split(/\s+/);
  return (parts.map((p) => p[0]).join("").slice(0, 2) || "?").toUpperCase();
}

type PendingMove = { personKey: string; name: string };

export function SpendGroupMembers({
  group,
  groups,
  directory,
  canEdit,
  onChanged,
}: {
  group: SpendGroupRow;
  groups: SpendGroupRow[];
  directory: SpendDirectoryEntry[] | null;
  canEdit: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const [pending, setPending] = useState<PendingMove | null>(null);
  const groupName = (id: string | null) => groups.find((g) => g.groupId === id)?.name ?? null;

  const add = useMutation({
    mutationFn: (v: { personKey: string; move?: boolean }) => api.billing.spendLimits.addMember(group.groupId, v.personKey, v.move),
    onSuccess: async () => {
      setPending(null);
      await onChanged();
    },
    onError: (err, v) => {
      // Someone moved them into another group since the page loaded; the
      // refresh brings that group into `groups` for the confirm below.
      if (err instanceof ApiError && err.code === "already_in_group") {
        const who = directory?.find((p) => p.personKey === v.personKey);
        setPending({ personKey: v.personKey, name: who?.name ?? "" });
        void onChanged();
      }
    },
  });
  const remove = useMutation({
    mutationFn: (personKey: string) => api.billing.spendLimits.removeMember(group.groupId, personKey),
    onSuccess: () => onChanged(),
  });

  const memberKeys = new Set(group.members.map((m) => m.personKey));
  const addable = (directory ?? []).filter((p) => !memberKeys.has(p.personKey));

  const pick = (personKey: string) => {
    const who = directory?.find((p) => p.personKey === personKey);
    if (!who) return;
    if (who.groupId && who.groupId !== group.groupId && groupName(who.groupId)) setPending({ personKey, name: who.name });
    else add.mutate({ personKey });
  };

  const pendingFrom =
    (pending && groups.find((g) => g.groupId !== group.groupId && g.members.some((m) => m.personKey === pending.personKey))?.name) ??
    "another group";

  const error = (add.error && !(add.error instanceof ApiError && add.error.code === "already_in_group") ? add.error : null) ?? remove.error;

  return (
    <div className="flex flex-col gap-2.5 px-4 pt-1 pb-3.5">
      <ul className="flex flex-wrap gap-1.5" aria-label={`People in ${group.name}`}>
        {group.members.map((m) => (
          <li
            key={m.personKey}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border bg-background py-0.5 pl-0.5 text-[12.5px]",
              canEdit ? "pr-1" : "pr-2.5",
            )}
          >
            <span className="grid size-5 place-items-center rounded-full bg-muted text-[9.5px] font-semibold text-muted-foreground">
              {initials(m.name)}
            </span>
            {m.name}
            {canEdit && (
              <button
                type="button"
                aria-label={`Remove ${m.name}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(m.personKey)}
                className="relative grid size-4 place-items-center rounded-full text-muted-foreground hover:text-destructive after:absolute after:-inset-1.5"
              >
                <X className="size-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canEdit &&
        (pending ? (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[13px] dark:bg-amber-400/10">
            <span>
              {pending.name} is in {pendingFrom}. Move to {group.name}? Spending so far stays with {pendingFrom}.
            </span>
            <Button size="sm" disabled={add.isPending} onClick={() => add.mutate({ personKey: pending.personKey, move: true })}>
              Move
            </Button>
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setPending(null)}>
              Cancel
            </Button>
          </div>
        ) : (
          <SpendPersonPicker
            id={`add-${group.groupId}`}
            people={addable}
            value=""
            onChange={pick}
            placeholder="Add a person…"
            ariaLabel={`Add a person to ${group.name}`}
            groupName={(id) => (id && id !== group.groupId ? groupName(id) : null)}
            disabled={add.isPending}
            className="w-64 max-w-full"
          />
        ))}
      {error && <span className="text-[13px] text-destructive">{error.message}</span>}
    </div>
  );
}
